import { Component, Input, OnInit } from '@angular/core';
import { UntypedFormBuilder } from '@angular/forms';
import { first } from 'rxjs';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { TranslocoService } from '@jsverse/transloco';
import { PageChangeEvent } from '@progress/kendo-angular-grid';
import { SortDescriptor } from '@progress/kendo-data-query';
import { IFilter, IGridResult, IGridState, PageSize } from 'src/app/core/models/base';
import { IEmployee } from 'src/app/employee/employee';
import { downloadBlob } from 'src/app/shared/helpers/fileHelper';
import { toDayBoundaryIso } from 'src/app/shared/helpers/date-helpers';
import { ExpenseService } from '../../services/expense.service';
import {
  ExpenseStatus,
  ExportFormat,
  IExpense,
  IExportExpensesRequest,
  ISearchExpensesRequest,
  expenseSortToOrderBy
} from '../../models/expense';

@UntilDestroy()
@Component({
    selector: 'app-expense-processed-list',
    templateUrl: './expense-processed-list.component.html',
    standalone: false
})
export class ExpenseProcessedListComponent implements OnInit {

  // Whether the detail dialog may re-decide (accept/reject) an already processed expense. Only
  // reviewers (supervisors/HR) may; a pure accounting user sees the list read-only.
  @Input() allowReprocess = false;

  expenses: IGridResult<IExpense[]> = { data: [], total: 0 };
  selectedIds: string[] = [];

  // Server-side paging state (mirrors the contact list).
  gridState: IGridState = { pageSize: 25, skip: 0 };

  // Server-side sort (date, submitter, expense type, status); the export follows the same order.
  sort: SortDescriptor[] = [{ field: 'date', dir: 'desc' }];

  // The (paged) filter request behind the currently displayed list — reused for reloads and,
  // with the page size widened, for "export all filtered".
  private lastRequest: ISearchExpensesRequest | null = null;

  get pageSizes() {
    return PageSize;
  }

  // dialog state (read-only view)
  dialogOpen = false;
  dialogId: string | null = null;

  ExpenseStatus = ExpenseStatus;

  // Only the employees the current user is authorized to control (subordinates + self, or all for HR).
  employees: IEmployee[] = [];

  // The processed statuses this list shows; the status filter narrows to one of them, null = all.
  private readonly processedStatuses = [ExpenseStatus.Accepted, ExpenseStatus.Rejected, ExpenseStatus.Paid];

  // Primitive values with a translated text (Kendo requires textField and valueField together).
  // Texts are filled in ngOnInit once the scope is loaded.
  statusFilterItems: { value: ExpenseStatus; text: string }[] = [];
  allStatusesItem: { value: ExpenseStatus | null; text: string } = { value: null, text: '' };

  filterForm = this.fb.group({
    employee: [null],
    status: [null],
    assignmentId: [null],
    dateRangeStart: [null],
    dateRangeEnd: [null]
  });

  constructor(
    private expenseService: ExpenseService,
    private fb: UntypedFormBuilder,
    private translocoService: TranslocoService
  ) { }

  ngOnInit(): void {
    this.statusFilterItems = this.processedStatuses.map(value => ({
      value,
      text: this.translocoService.translate('expenses.expenseStates.' + ExpenseStatus[value])
    }));
    this.allStatusesItem = { value: null, text: this.translocoService.translate('expenses.filterAllStatuses') };

    this.expenseService.getReviewableEmployees()
      .pipe(first(), untilDestroyed(this))
      .subscribe(employees => this.employees = employees);

    this.onSubmitFilter();
  }

  onSubmitFilter(): void {
    this.selectedIds = [];
    // A new filter always restarts at the first page.
    this.gridState.skip = 0;
    this.lastRequest = this.buildRequest(1, this.gridState.pageSize);
    this.loadList(this.lastRequest);
  }

  onResetFilter(): void {
    this.filterForm.reset();
    this.onSubmitFilter();
  }

  // --- paging (server-side) ---
  onPageChange({ skip, take }: PageChangeEvent): void {
    this.gridState.skip = skip;
    this.gridState.pageSize = take;
    // Selection is per-page; drop it when the visible rows change.
    this.selectedIds = [];
    this.lastRequest = this.buildRequest(Math.round((skip + take) / take), take);
    this.loadList(this.lastRequest);
  }

  onPageSizeChange(pageSize: number): void {
    this.onPageChange({ skip: this.gridState.skip, take: pageSize });
  }

  onGridReload(): void {
    if (this.lastRequest) {
      this.loadList(this.lastRequest);
    }
  }

  // --- sorting (server-side) ---
  onSortChange(sort: SortDescriptor[]): void {
    this.sort = sort;
    this.gridState.skip = 0;
    this.selectedIds = [];
    this.lastRequest = this.buildRequest(1, this.gridState.pageSize);
    this.loadList(this.lastRequest);
  }

  // Dropdown items (Excel / PDF) for both export buttons.
  exportFormatItems = [
    { text: 'Excel', format: ExportFormat.Excel },
    { text: 'PDF', format: ExportFormat.Pdf }
  ];

  // Export the whole currently filtered list in the chosen format.
  exportAll(item: { format: ExportFormat }): void {
    this.export(null, item.format);
  }

  // Export only the checked rows in the chosen format.
  exportSelected(item: { format: ExportFormat }): void {
    if (!this.selectedIds.length) {
      return;
    }
    this.export(this.selectedIds, item.format);
  }

  private export(selectedExpenseIds: string[] | null, format: ExportFormat): void {
    const request: IExportExpensesRequest = {
      ...(this.lastRequest ?? this.buildRequest(1, this.gridState.pageSize)),
      // Export the whole filtered set, not just the current page.
      pageNumber: 1,
      pageSize: 999999,
      selectedExpenseIds: selectedExpenseIds ?? undefined,
      format
    };

    const fileName = format === ExportFormat.Pdf ? 'Spesen.pdf' : 'Spesen.xlsx';

    this.expenseService.exportExpenses(request)
      .pipe(first(), untilDestroyed(this))
      .subscribe(response => downloadBlob(response, fileName));
  }

  private buildRequest(pageNumber: number, pageSize: number): ISearchExpensesRequest {
    const formValues = this.filterForm.value;
    const status: ExpenseStatus | null = formValues.status ?? null;

    const request: ISearchExpensesRequest = {
      pageNumber,
      pageSize,
      orderBy: expenseSortToOrderBy(this.sort),
      forApproval: true,
      includeOwn: true,
      statuses: status == null ? this.processedStatuses : [status]
    };

    const filters: IFilter[] = [];

    if (formValues.employee?.id) {
      filters.push({ field: 'SubmitterId', operator: 'eq', value: formValues.employee.id });
    }

    if (formValues.assignmentId) {
      // Filter via the non-nullable Assignment.Id navigation key. The nullable FK column
      // (LinkedAssignmentId, Guid?) is not handled by the backend's generic filter value
      // converter and throws a JsonElement cast error.
      filters.push({ field: 'LinkedAssignment.Id', operator: 'eq', value: formValues.assignmentId });
    }

    if (formValues.dateRangeStart) {
      filters.push({ field: 'Date', operator: 'gte', value: toDayBoundaryIso(formValues.dateRangeStart, false) });
    }

    if (formValues.dateRangeEnd) {
      filters.push({ field: 'Date', operator: 'lte', value: toDayBoundaryIso(formValues.dateRangeEnd, true) });
    }

    if (filters.length === 1) {
      request.advancedFilter = filters[0];
    } else if (filters.length > 1) {
      request.advancedFilter = { logic: 'and', filters };
    }

    return request;
  }

  private loadList(request: ISearchExpensesRequest): void {
    this.expenseService.searchExpensesForApproval(request)
      .pipe(first(), untilDestroyed(this))
      .subscribe(response => this.expenses = { data: response.data, total: response.totalCount });
  }

  statusName(status: ExpenseStatus): string {
    return ExpenseStatus[status];
  }

  statusClass(status: ExpenseStatus): string {
    return 'status-' + ExpenseStatus[status].toLowerCase();
  }

  open(expense: IExpense): void {
    this.dialogId = expense.id;
    this.dialogOpen = true;
  }

  closeDialog(): void {
    this.dialogOpen = false;
    this.dialogId = null;
  }
}
